"use client";

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useRouter, usePathname, useParams } from 'next/navigation';
import { translations } from './translations';

export type Language = 'en' | 'sq';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}


const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const params = useParams();
  const langParam = params?.lang as string;
  const initialLang = (langParam === 'en' || langParam === 'sq') ? langParam : 'sq';
  const [language, setLocalLanguage] = useState<Language>(initialLang);
  const router = useRouter();
  const pathname = usePathname();

  // Sync state if URL changes externally
  useEffect(() => {
    if (langParam === 'en' || langParam === 'sq') {
      if (langParam !== language) setLocalLanguage(langParam as Language);
    }
  }, [langParam]);

  const setLanguage = (newLang: Language) => {
    if (newLang === language) return;

    // Save language preference in cookie for middleware
    document.cookie = `preferred_language=${newLang}; path=/; max-age=31536000`; // 1 year

    // Update local state to trigger translations without remounting the page
    setLocalLanguage(newLang);

    // Use Next.js router to navigate to the new locale path so Server Components update correctly
    const segments = window.location.pathname.split('/');
    if (segments.length >= 2 && (segments[1] === 'en' || segments[1] === 'sq')) {
      segments[1] = newLang;
    }
    const newPath = segments.join('/') || `/${newLang}`;
    router.push(newPath + window.location.search);
  };

  const t = (key: string) => {
    const translationSet = translations[language as keyof typeof translations] as unknown as Record<string, string>;
    if (translationSet && key in translationSet) {
      return translationSet[key];
    }
    return key; // fallback to key
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    // Provide a fallback for SSR or when used outside of LanguageProvider
    return {
      language: 'sq' as Language,
      setLanguage: () => { },
      t: (key: string) => key
    };
  }
  return context;
}
